import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { History as HistoryIcon } from "lucide-react";

const inputCls = "bg-obsidian border border-white/10 rounded-md text-white px-3 py-2 text-sm focus:border-gold outline-none";

const statusBadge = {
  sent: { label: "Enviado", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  failed: { label: "Fallido", cls: "bg-crimson/15 text-crimson border-crimson/30" },
  skipped: { label: "Omitido (duplicado)", cls: "bg-white/5 text-muted-foreground border-white/10" },
};

const typeLabel = (type, thresholdDays) => (type === "upcoming" ? `Próximo (${thresholdDays}d)` : "Vencido");
const recipientLabel = (r) => (r.recipientKind === "group" ? `Grupo: ${r.recipientName || r.groupName}` : `Admin: ${r.recipientName || r.recipientId}`);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function MembershipHistory({ groups }) {
  const [chatId, setChatId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.membershipHistory(chatId || undefined));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar historial");
    } finally {
      setLoading(false);
    }
  }, [chatId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="card-surface overflow-hidden" data-testid="membership-history-card">
      <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <HistoryIcon className="text-gold" size={16} />
          <span className="overline">Historial de avisos</span>
        </div>
        <select data-testid="membership-history-filter" className={inputCls} value={chatId} onChange={(e) => setChatId(e.target.value)}>
          <option value="">Todos los grupos</option>
          {groups.map((g) => (
            <option key={g.chatId} value={g.chatId}>{g.name || g.chatId}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm" data-testid="membership-history-empty">Aún no se ha registrado ningún aviso.</div>
      ) : (
        <div className="divide-y divide-white/5 max-h-[560px] overflow-y-auto">
          {rows.map((r, i) => {
            const b = statusBadge[r.status] || statusBadge.failed;
            return (
              <div key={`${r.recipientId}-${r.createdAt}-${i}`} className="flex flex-col sm:flex-row sm:items-center gap-2 px-5 py-3 text-sm" data-testid={`membership-history-row-${i}`}>
                <div className="w-40 shrink-0 text-xs text-muted-foreground font-mono">{fmt(r.createdAt)}</div>
                <div className="flex-1 min-w-0 truncate">{r.groupName || r.chatId}</div>
                <div className="w-32 shrink-0 text-xs">{typeLabel(r.type, r.thresholdDays)}</div>
                <div className="flex-1 min-w-0 truncate text-xs text-muted-foreground">{recipientLabel(r)}</div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border shrink-0 ${b.cls}`} title={r.error || ""}>{b.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
